import React, { useState } from 'react';
import { Dialog, Box, Typography, CircularProgress } from '@mui/material';
import { useTranslate } from 'src/locales';
import ByzMuiButton from 'src/components/core-components/mui/buttons/ByzMuiButton';

interface IsolationFinishConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
  title?: string;
  content?: string;
}

const IsolationFinishConfirmDialog: React.FC<IsolationFinishConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
}) => {
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const success = await onConfirm();
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Confirm error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '409px',
          borderRadius: '16px',
          boxShadow: '0px 8px 36px rgba(0, 0, 0, 0.16)',
          overflow: 'hidden',
          position: 'relative',
          height: '307px',
          margin: 'auto',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            top: '8px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
            }}
          >
            <img
              src="/assets/icons/info/yellow-info.svg"
              alt="Info"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '19px',
              fontWeight: 700,
              lineHeight: '24px',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '298px',
              textAlign: 'center',
              fontFamily: 'Public Sans',
            }}
          >
            {title || t('isolation-requests.finish-confirm-title') || 'Uyarı Mesajı'}
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            position: 'absolute',
            top: '124px',
            left: 0,
            width: '100%',
            padding: '8px 24px',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '21px',
              color: '#000',
              fontFamily: 'Public Sans',
            }}
          >
            {content || t('isolation-requests.finish-confirm-content') || 'İzolasyon süreci tamamlanacak. Devam etmek istiyor musunuz?'}
          </Typography>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 48px)',
            display: 'flex',
            alignItems: 'center',
            padding: '16px 24px 24px',
            boxSizing: 'border-box',
            gap: '8px',
          }}
        >
          <Box sx={{ flex: 1, height: '42px', position: 'relative' }}>
            <ByzMuiButton
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={loading}
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                border: '1px solid rgba(86, 103, 137, 0.26)',
                fontSize: '14px',
                fontWeight: 600,
                color: 'rgba(21, 25, 32, 0.5)',
                fontFamily: 'Work Sans',
              }}
            >
              {t('buttons.cancel') || 'İptal'}
            </ByzMuiButton>
          </Box>
          <Box sx={{ flex: '0.8187', height: '42px', position: 'relative' }}>
            <ByzMuiButton
              onClick={handleConfirm}
              disabled={loading}
              differentColor="blue"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                backgroundColor: '#136dc0',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Work Sans',
                '&:hover': {
                  backgroundColor: '#0d5a9f',
                },
                '&:disabled': {
                  backgroundColor: '#136dc0',
                  opacity: 0.6,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                'Tamam'
              )}
            </ByzMuiButton>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default IsolationFinishConfirmDialog;

